import { EarningsItemBase, Entity, mapEntity } from '@api';
import { Icon } from '@design/components/icon/Icon';
import {
  Formats,
  formatDateToLocale,
  formatDateToUtcIsoString,
  formatPercentage,
  getCurrencySymbol,
} from '@helpers';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { useFetchSnakeByName } from '@terminal/react-query-hooks/snakes/useFetchSnakeByName';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import {
  getOneDayReactionDate,
  getOneDayReactionValue,
  getReportingTimeIcon,
} from '@terminal/widgets/earnings/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EarningsInsightSection.module.scss';
import { formatCurrency, getMetricValueColor, getVariant } from './utils';

const ONE_DAY_RETURN_SNAKE = 'mom(horiz=1d)';

export interface RecapContentProps {
  earningsData: EarningsItemBase;
  entity: Entity;
}

export const RecapContent = ({ earningsData, entity }: RecapContentProps) => {
  const { t } = useTranslation(['widget']);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const mappedEntity = useMemo(() => mapEntity(entity), [entity]);
  const { oneDayLiveReturn } = useOneDayReturnLivePrice(mappedEntity);

  const currencySymbol = getCurrencySymbol(earningsData.currency);
  const reportingDate = earningsData.reporting_date
    ? new Date(earningsData.reporting_date)
    : undefined;

  const formattedDate = reportingDate
    ? formatDateToLocale(languageSetting, reportingDate, Formats['PP'])
    : '-';

  const dateToUse = useMemo(
    () => getOneDayReactionDate(reportingDate),
    [reportingDate]
  );

  const formattedFromDate = dateToUse
    ? formatDateToUtcIsoString(dateToUse)
    : undefined;

  const { data: activeSnakeData } = useFetchSnakeByName(
    `${entity.tag}.${ONE_DAY_RETURN_SNAKE}`,
    undefined,
    formattedFromDate,
    !formattedFromDate
  );

  const oneDayReaction = useMemo(
    () =>
      getOneDayReactionValue(
        dateToUse,
        oneDayLiveReturn,
        activeSnakeData,
        formattedFromDate
      ),
    [dateToUse, oneDayLiveReturn, activeSnakeData, formattedFromDate]
  );

  const epsValue = earningsData.eps?.value;
  const epsSurprise = earningsData.eps?.surprise;

  const timeIcon = reportingDate
    ? getReportingTimeIcon(reportingDate)
    : undefined;

  const epsSurpriseVariant = getVariant(epsSurprise);
  const oneDayReactionVariant = getVariant(oneDayReaction);

  return (
    <>
      <div className={styles.metricContainer}>
        <span className={styles.metricLabel}>
          {t('widget:assetOverview.earningsSection.reportedOn')}
        </span>
        <div className={styles.dateContainer}>
          <span className={styles.metricValue}>{formattedDate}</span>
          {timeIcon && (
            <Icon className={styles.timeIcon} iconName={timeIcon} size={16} />
          )}
        </div>
      </div>
      <div className={styles.metricContainer}>
        <span className={styles.metricLabel}>
          {t('widget:assetOverview.earningsSection.epsActual')}
        </span>
        <span className={styles.metricValueMono}>
          {formatCurrency({
            value: epsValue,
            currencySymbol,
            showPositiveSign: false,
          })}
        </span>
      </div>
      <div className={styles.metricContainer}>
        <span className={styles.metricLabel}>
          {t('widget:assetOverview.earningsSection.epsSurprise')}
        </span>
        <span
          className={styles.metricValueMono}
          style={{ color: getMetricValueColor(epsSurpriseVariant) }}
        >
          {formatCurrency({
            value: epsSurprise,
            currencySymbol,
            showPositiveSign: true,
          })}
        </span>
      </div>
      <div className={styles.metricContainer}>
        <span className={styles.metricLabel}>
          {t('widget:assetOverview.earningsSection.oneDayReaction')}
        </span>
        <span
          className={styles.metricValueMono}
          style={{ color: getMetricValueColor(oneDayReactionVariant) }}
        >
          {oneDayReaction !== undefined
            ? formatPercentage(oneDayReaction, {
                suffix: '%',
                addPlus: true,
                useOriginalValue: true,
              })
            : '-'}
        </span>
      </div>
    </>
  );
};
